import {
  emptyCheckItemMedia,
  type CheckItem,
  type DatabaseFile,
  type PendingItemAudio,
  type PendingItemImage,
  type PendingItemVideo,
} from '../../types';
import { newAudioFilename } from '../../utils/itemAudio';
import { newVideoFilename } from '../../utils/itemVideo';
import { newImageFilename } from '../../utils/itemImages';
import { escapeAttr, escapeHtml } from '../../utils/escapeHtml';
import { formatSpanishGuillemetsForDisplay } from '../../utils/spanishDisplayQuotes';
import { splitItemText } from '../../utils/itemTextParts';

export const TODAY = new Date().toISOString().split('T')[0]!;
export const LIST_ITEM_TITLE_MAX = 50;

function truncateListText(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}...`;
}

/** List label: first line through the first period, max 50 characters (nothing else shown). */
export function listItemDisplayTitle(text: string): string {
  const firstLine = splitItemText(text).title;
  const periodIdx = firstLine.indexOf('.');
  let upToPeriod = periodIdx >= 0 ? firstLine.slice(0, periodIdx).trim() : firstLine.trim();
  if (!upToPeriod && periodIdx >= 0) upToPeriod = firstLine.trim();
  return truncateListText(upToPeriod, LIST_ITEM_TITLE_MAX);
}

function listItemHasHiddenText(text: string): boolean {
  const normalized = text.trim().replace(/\r\n/g, '\n');
  const firstLine = splitItemText(text).title;
  const periodIdx = firstLine.indexOf('.');
  const fullLabel =
    periodIdx >= 0 ? firstLine.slice(0, periodIdx).trim() : firstLine.trim();
  return (
    fullLabel.length > LIST_ITEM_TITLE_MAX ||
    periodIdx >= 0 ||
    normalized.includes('\n')
  );
}

export function collectPendingFromInput(input: HTMLInputElement): PendingItemImage[] {
  const out: PendingItemImage[] = [];
  for (const file of input.files ?? []) {
    if (!file.type.startsWith('image/')) continue;
    out.push({ file, filename: newImageFilename(file) });
  }
  return out;
}

export function collectPendingAudioFromInput(input: HTMLInputElement): PendingItemAudio[] {
  const out: PendingItemAudio[] = [];
  for (const file of input.files ?? []) {
    if (!file.type.startsWith('audio/')) continue;
    out.push({ file, filename: newAudioFilename(file) });
  }
  return out;
}

const VIDEO_PICK_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv', 'mkv', '3gp', 'avi']);
const AUDIO_PICK_EXTENSIONS = new Set(['mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac']);

/** True when a file input entry should be treated as video (some browsers omit MIME). */
export function isVideoPickFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_PICK_EXTENSIONS.has(ext);
}

function isAudioPickFile(file: File): boolean {
  if (file.type.startsWith('audio/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return AUDIO_PICK_EXTENSIONS.has(ext);
}

export function collectPendingVideoFromInput(
  input: HTMLInputElement,
  options?: { lenient?: boolean }
): PendingItemVideo[] {
  const out: PendingItemVideo[] = [];
  for (const file of input.files ?? []) {
    if (isVideoPickFile(file)) {
      out.push({ file, filename: newVideoFilename(file) });
      continue;
    }
    if (
      options?.lenient &&
      !isAudioPickFile(file) &&
      !file.type.startsWith('image/')
    ) {
      out.push({ file, filename: newVideoFilename(file) });
    }
  }
  return out;
}

/** Backfill media arrays on items parsed before attachment fields existed. */
export function ensureItemMediaFields(item: CheckItem): void {
  const base = emptyCheckItemMedia();
  if (!Array.isArray(item.images)) item.images = base.images;
  if (!Array.isArray(item.pendingImages)) item.pendingImages = base.pendingImages;
  if (!Array.isArray(item.imagesToDelete)) item.imagesToDelete = base.imagesToDelete;
  if (!Array.isArray(item.audio)) item.audio = base.audio;
  if (!Array.isArray(item.pendingAudio)) item.pendingAudio = base.pendingAudio;
  if (!Array.isArray(item.audioToDelete)) item.audioToDelete = base.audioToDelete;
  if (!Array.isArray(item.video)) item.video = base.video;
  if (!Array.isArray(item.pendingVideo)) item.pendingVideo = base.pendingVideo;
  if (!Array.isArray(item.videoToDelete)) item.videoToDelete = base.videoToDelete;
  if (!Array.isArray(item.videoRenames)) item.videoRenames = base.videoRenames;
}

/** Audio files chosen on the video picker (e.g. MP3 recordings). */
export function collectAudioFromVideoInput(input: HTMLInputElement): PendingItemAudio[] {
  const out: PendingItemAudio[] = [];
  for (const file of input.files ?? []) {
    if (!isAudioPickFile(file)) continue;
    out.push({ file, filename: newAudioFilename(file) });
  }
  return out;
}

export function itemHasUnsavedMediaChanges(item: CheckItem): boolean {
  return (
    item.pendingImages.length > 0 ||
    item.pendingAudio.length > 0 ||
    item.pendingVideo.length > 0 ||
    item.imagesToDelete.length > 0 ||
    item.audioToDelete.length > 0 ||
    item.videoToDelete.length > 0 ||
    item.videoRenames.length > 0
  );
}

export function fileHasUnsavedMediaChanges(file: DatabaseFile): boolean {
  if (
    file.orphanedImagePaths.length > 0 ||
    file.orphanedAudioPaths.length > 0 ||
    file.orphanedVideoPaths.length > 0
  ) {
    return true;
  }
  return file.sections.some(s => s.items.some(itemHasUnsavedMediaChanges));
}

export function renderItemTextHtml(item: CheckItem, formatSpanishQuotes = false): string {
  const raw = listItemDisplayTitle(item.text);
  const label = formatSpanishQuotes ? formatSpanishGuillemetsForDisplay(raw) : raw;
  return `<span class="item-body">${escapeHtml(label)}</span>`;
}

/** Native tooltip with full text when the list hides content after the period. */
export function itemTextTooltipAttr(item: CheckItem, formatSpanishQuotes = false): string {
  if (!listItemHasHiddenText(item.text)) return '';
  const normalized = item.text.trim().replace(/\r\n/g, '\n');
  const display = formatSpanishQuotes ? formatSpanishGuillemetsForDisplay(normalized) : normalized;
  return ` title="${escapeAttr(display.replace(/\n/g, ' '))}"`;
}
